import { ReactElement, useMemo } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import {
  CommentType,
  commentColorOptions,
  commentTypeLabels,
  commentTypes,
  getCommentTypeCapabilities,
} from '@constants'
import { reject, sortBy, toNumber } from 'lodash-es'
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import { storeyToLabel } from '@editor/pages/Plans/components/AddPlanDialog/components/utils'
import { CheckboxLabeled } from '@ui/actions'
import { CommentColorSelect } from '@ui/forms'
import { roofStoreyKey, useModelStore } from '@editorStores'
import { CommentSettingsFormValues } from './commentSettingsForm'

const isRoofStorey = (storey: string) => storey === roofStoreyKey

interface Props {
  /** Whether the `custom` type stays selectable (true only for comments that are already custom, so
   *  the derived types don't expose `custom` as a new choice). */
  allowCustomType: boolean
  /** Lock the type selector (e.g. for element-linked comments, whose type is fixed). */
  disableType?: boolean
}

/**
 * The comment settings form fields (type, storeys for plan creation, and — for custom comments —
 * colour / scene storey / title / plan visibility). RHF-connected: reads/writes the form context
 * provided by its parent, shared by the in-scene `CommentEditPopup` and the `CommentDrawer`.
 */
const CommentSettingsFields = ({ allowCustomType, disableType = false }: Props): ReactElement => {
  const { control, setValue } = useFormContext<CommentSettingsFormValues>()

  const { field: typeField } = useController({ control, name: 'type' })
  const { field: storeysField } = useController({ control, name: 'storeysForPlans' })
  const { field: colorField } = useController({ control, name: 'color' })
  const { field: backgroundColorField } = useController({ control, name: 'backgroundColor' })
  const { field: sceneStoreyField } = useController({ control, name: 'storeyForSceneRendering' })
  const { field: showTitleField } = useController({ control, name: 'showTitle' })
  const { field: positionPlanField } = useController({ control, name: 'showInPlans.positionPlan' })
  const { field: stiffeningPlanField } = useController({
    control,
    name: 'showInPlans.stiffeningPlan',
  })
  const { field: loadPlanField } = useController({ control, name: 'showInPlans.loadPlan' })

  // Which fields the current type lets the user edit (everything else is derived from the type).
  const capabilities = useMemo(() => getCommentTypeCapabilities(typeField.value), [typeField.value])

  const typeOptions = useMemo(
    () => (allowCustomType ? commentTypes : commentTypes.filter(type => type !== 'custom')),
    [allowCustomType],
  )

  // The roof storey can't be part of an Aussteifungsplan, so it is hidden as a selectable storey
  // for that type (and stripped when switching to it, see `handleTypeChange`).
  const roofStoreyDisallowed = typeField.value === 'stiffening-plan'

  const availableStoreys = useModelStore(state => state.availableStoreys)
  const sortedStoreys = useMemo(() => {
    const nonRoof = reject(Array.from(availableStoreys), isRoofStorey)
    const sorted = sortBy(nonRoof, s => toNumber(s))
    return roofStoreyDisallowed ? sorted : [...sorted, roofStoreyKey]
  }, [availableStoreys, roofStoreyDisallowed])

  const storeysForPlans = storeysField.value ?? []
  const toggleStorey = (storey: string) =>
    storeysField.onChange(
      storeysForPlans.includes(storey)
        ? storeysForPlans.filter(s => s !== storey)
        : [...storeysForPlans, storey],
    )

  const handleTypeChange = (nextType: CommentType) => {
    typeField.onChange(nextType)
    // Aussteifungsplan can't include the roof storey — drop it if it was previously selected.
    if (nextType === 'stiffening-plan') {
      storeysField.onChange(storeysForPlans.filter(storey => !isRoofStorey(storey)))
    }
  }

  // The color picker works on `commentColorOptions` keys; fall back to a synthetic key for colors
  // that aren't one of the presets so the Select stays controlled.
  const selectedColorKey = useMemo(() => {
    const match = commentColorOptions.find(
      option =>
        option.color === colorField.value && option.backgroundColor === backgroundColorField.value,
    )
    return match?.key ?? `${colorField.value}-${backgroundColorField.value}`
  }, [colorField.value, backgroundColorField.value])

  const handleColorChange = (key: string) => {
    const option = commentColorOptions.find(o => o.key === key)
    if (!option) return
    // Set the background silently, then change the color so a single submit carries both.
    setValue('backgroundColor', option.backgroundColor, { shouldDirty: true })
    colorField.onChange(option.color)
  }

  return (
    <Stack spacing={1.5}>
      <Stack spacing={0.25}>
        <Typography variant="caption" color="text.secondary">
          Typ
        </Typography>
        <Select
          value={typeField.value}
          size="small"
          fullWidth
          disabled={disableType}
          onChange={event => handleTypeChange(event.target.value as CommentType)}
          data-cy="comment-popup-type"
        >
          {typeOptions.map(commentType => (
            <MenuItem key={commentType} value={commentType}>
              {commentTypeLabels[commentType]}
            </MenuItem>
          ))}
        </Select>
      </Stack>

      <Stack spacing={0.25}>
        <Typography variant="caption" color="text.secondary">
          Stockwerke für Planerstellung
        </Typography>
        <FormGroup row>
          {sortedStoreys.map(storey => (
            <FormControlLabel
              key={storey}
              control={
                <Checkbox
                  size="small"
                  checked={storeysForPlans.includes(storey)}
                  onChange={() => toggleStorey(storey)}
                />
              }
              label={<Typography variant="body2">{storeyToLabel(storey)}</Typography>}
            />
          ))}
        </FormGroup>
      </Stack>

      {/* Color, scene storey and title are derived from the type for the non-custom types, so they
          are only editable for `custom`. */}
      {capabilities.appearance && (
        <Stack spacing={0.75} data-cy="comment-custom-settings">
          <Stack spacing={0.25}>
            <Typography variant="caption" color="text.secondary">
              SW für 3D Ansicht
            </Typography>
            <Select
              value={sceneStoreyField.value ?? ''}
              size="small"
              fullWidth
              displayEmpty
              onChange={event => sceneStoreyField.onChange(event.target.value || null)}
              data-cy="comment-popup-storey-for-scene-rendering"
            >
              <MenuItem value="">
                <em>Keines</em>
              </MenuItem>
              {sortedStoreys.map(storey => (
                <MenuItem key={storey} value={storey}>
                  {storeyToLabel(storey)}
                </MenuItem>
              ))}
            </Select>
          </Stack>
          <Stack spacing={0.25}>
            <Typography variant="caption" color="text.secondary">
              Farbe
            </Typography>
            <CommentColorSelect value={selectedColorKey} onChange={handleColorChange} fullWidth />
          </Stack>
          <CheckboxLabeled
            checked={showTitleField.value}
            onChange={event => showTitleField.onChange(event.target.checked)}
            label="Text in Szene anzeigen"
          />
        </Stack>
      )}

      {/* "In Plänen anzeigen" is editable for custom and Notiz comments; the plan-specific types
          derive it from their type. */}
      {capabilities.showInPlans && (
        <Stack spacing={0.25} data-cy="comment-show-in-plans">
          <Typography variant="caption" color="text.secondary">
            In Plänen anzeigen
          </Typography>
          <Stack>
            <CheckboxLabeled
              checked={positionPlanField.value}
              onChange={event => positionPlanField.onChange(event.target.checked)}
              label="Positionsplan"
            />
            <CheckboxLabeled
              checked={stiffeningPlanField.value}
              onChange={event => stiffeningPlanField.onChange(event.target.checked)}
              label="Aussteifungsplan"
            />
            <CheckboxLabeled
              checked={loadPlanField.value}
              onChange={event => loadPlanField.onChange(event.target.checked)}
              label="Lastplan"
            />
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}

export default CommentSettingsFields
